import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "@/lib/toast";
import { Trash2, X } from "lucide-react";
import api from "@/lib/api";
import { PageHeader, Card, Table, StatusBadge, Field, Select, SecondaryButton, SortableHeader, useTableSort } from "@/components/admin/AdminUI";
import { formatDate } from "@/lib/format";
import { ModalScroll } from "@/components/public/ModalScroll";

const STATUS_OPTIONS = [
  { key: "new", label: "New" },
  { key: "under_review", label: "Under review" },
  { key: "confirmed", label: "Confirmed" },
  { key: "rejected", label: "Rejected" },
  { key: "resolved", label: "Resolved" },
];

export default function AdminReports() {
  const qc = useQueryClient();
  const [statusFilter, setStatusFilter] = useState("");
  const [open, setOpen] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const { data: reports = [] } = useQuery({
    queryKey: ["reports", statusFilter],
    queryFn: async () => (await api.get("/admin/false-advertising-reports", { params: { status: statusFilter || undefined } })).data,
  });
  const { sort, toggleSort, sortRows } = useTableSort();
  const sortedReports = sortRows(reports, (r, key) => {
    if (key === "reporter") return r.reporter_name || r.reporter_email || "";
    if (key === "evidence_count") return r.evidence_file_urls?.length || 0;
    if (key === "created_at") return r.created_at ? new Date(r.created_at).getTime() : null;
    return r[key] ?? null;
  });

  async function setStatus(id, status) {
    await api.patch(`/admin/false-advertising-reports/${id}`, { status });
    qc.invalidateQueries({ queryKey: ["reports"] });
  }

  async function clearReports() {
    const scope = statusFilter ? `'${statusFilter}'` : "ALL";
    const msg = `Delete ${reports.length} ${scope} false-advertising report${reports.length === 1 ? "" : "s"}?\n\nThis only clears reports submitted via the "Report a label mismatch" form. General contacts (on the Contacts page) will NOT be touched. Brand sustainability scores will be recomputed.`;
    if (!window.confirm(msg)) return;
    setDeleting(true);
    try {
      const { data } = await api.delete("/admin/false-advertising-reports", {
        params: { status: statusFilter || undefined },
      });
      toast.success(`Cleared ${data.deleted} report${data.deleted === 1 ? "" : "s"}.`);
      qc.invalidateQueries({ queryKey: ["reports"] });
      qc.invalidateQueries({ queryKey: ["admin-brands"] });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to clear reports");
    } finally {
      setDeleting(false);
    }
  }

  return (
    <div>
      <PageHeader
        title="False advertising reports"
        subtitle={`${reports.length} reports`}
        actions={
          reports.length > 0 && (
            <SecondaryButton
              onClick={clearReports}
              disabled={deleting}
              data-testid="clear-reports-button"
              className="text-clay hover:text-clay/80 border-clay/30"
            >
              <Trash2 className="w-4 h-4 inline mr-1" />
              {deleting ? "Clearing…" : statusFilter ? `Clear ${statusFilter}` : "Clear reports"}
            </SecondaryButton>
          )
        }
      />
      <Card className="mb-4">
        <Field label="Filter status">
          <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="">All</option>
            {STATUS_OPTIONS.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
          </Select>
        </Field>
      </Card>
      <div className="bg-white border border-[#E9ECEF] rounded-lg overflow-hidden">
        <Table>
          <thead className="bg-[#F8F9FA] border-b border-[#E9ECEF] text-xs uppercase tracking-wider text-[#6C757D]">
            <tr>
              <SortableHeader label="Reporter" sortKey="reporter" sort={sort} onSort={toggleSort} />
              <SortableHeader label="Brand" sortKey="brand_name" sort={sort} onSort={toggleSort} />
              <SortableHeader label="Claim" sortKey="claim_description" sort={sort} onSort={toggleSort} />
              <SortableHeader label="Actual" sortKey="actual_material_description" sort={sort} onSort={toggleSort} />
              <SortableHeader label="Evidence" sortKey="evidence_count" sort={sort} onSort={toggleSort} />
              <SortableHeader label="Status" sortKey="status" sort={sort} onSort={toggleSort} />
              <SortableHeader label="Date" sortKey="created_at" sort={sort} onSort={toggleSort} />
            </tr>
          </thead>
          <tbody>
            {sortedReports.map((r) => (
              <tr
                key={r.id}
                className="border-b border-[#E9ECEF] cursor-pointer hover:bg-[#F8F9FA]"
                onClick={() => { setOpen(r); if (r.status === "new") setStatus(r.id, "under_review"); }}
                data-testid={`report-row-${r.id}`}
              >
                <td className="px-3 py-3">{r.reporter_name || "—"}<br /><span className="text-xs text-[#6C757D]">{r.reporter_email}</span></td>
                <td className="px-3 py-3">{r.brand_name || "—"}</td>
                <td className="px-3 py-3 text-xs max-w-xs truncate">{r.claim_description}</td>
                <td className="px-3 py-3 text-xs max-w-xs truncate">{r.actual_material_description}</td>
                <td className="px-3 py-3 text-xs">{r.evidence_file_urls?.length || 0}</td>
                <td className="px-3 py-3"><StatusBadge status={r.status} /></td>
                <td className="px-3 py-3 text-xs">{formatDate(r.created_at)}</td>
              </tr>
            ))}
            {sortedReports.length === 0 && <tr><td colSpan={7} className="px-3 py-12 text-center text-[#6C757D]">No reports.</td></tr>}
          </tbody>
        </Table>
      </div>

      {open && (
        <div
          className="fixed inset-0 z-40 bg-black/40 flex items-center justify-center p-4"
          onClick={(e) => { if (e.target === e.currentTarget) setOpen(null); }}
          data-testid="report-dialog"
        >
          <div className="bg-white border border-line shadow-2xl max-w-2xl w-full h-[90vh] flex flex-col overflow-hidden">
            <div className="px-6 pt-5 pb-3 border-b border-[#E9ECEF]">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h2 className="font-serif text-xl mb-1">{open.claim_description || "(no subject)"}</h2>
                  <div className="text-xs text-[#6C757D]">
                    {open.reporter_name || "—"} &lt;{open.reporter_email || "—"}&gt; · {formatDate(open.created_at)}
                  </div>
                  {open.brand_name && (
                    <div className="text-xs text-[#6C757D] mt-0.5">Brand: <strong>{open.brand_name}</strong>{open.clothing_item_name && ` · Item: ${open.clothing_item_name}`}</div>
                  )}
                  {open.item_url_on_site && (
                    <div className="text-xs mt-0.5">
                      <a href={open.item_url_on_site} target="_blank" rel="noopener noreferrer" className="text-forest underline">View item on site ↗</a>
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => setOpen(null)}
                  className="text-[#6C757D] hover:text-ink p-1"
                  aria-label="Close"
                  data-testid="report-dialog-close"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            <ModalScroll className="px-6 py-4">
              <div className="space-y-4">
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-[#6C757D] mb-1">Brand&apos;s claim</div>
                  <p className="text-sm whitespace-pre-wrap">{open.claim_description || "—"}</p>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-[#6C757D] mb-1">Actual / what they saw</div>
                  <p className="text-sm whitespace-pre-wrap">{open.actual_material_description || "—"}</p>
                </div>
                {open.evidence_file_urls?.length > 0 && (
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-[#6C757D] mb-1">Evidence ({open.evidence_file_urls.length})</div>
                    <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                      {open.evidence_file_urls.map((u, i) => (
                        <a key={u} href={u} target="_blank" rel="noopener noreferrer">
                          <img src={u} alt={`Evidence ${i + 1}`} className="aspect-square object-cover border border-line hover:opacity-80" />
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </ModalScroll>

            <div className="border-t border-[#E9ECEF] px-6 py-3">
              <div className="text-xs uppercase tracking-wider text-[#6C757D] mb-2">Set status</div>
              <div className="flex flex-wrap gap-2" data-testid="report-status-actions">
                {STATUS_OPTIONS.map((s) => (
                  <SecondaryButton
                    key={s.key}
                    disabled={open.status === s.key}
                    onClick={async () => {
                      await setStatus(open.id, s.key);
                      setOpen((o) => o ? { ...o, status: s.key } : o);
                    }}
                    data-testid={`report-set-${s.key}`}
                    className={open.status === s.key ? "ring-2 ring-forest" : ""}
                  >
                    {s.label}
                  </SecondaryButton>
                ))}
              </div>
              {open.reporter_email && (
                <a
                  href={`mailto:${open.reporter_email}?subject=Re:%20Label%20mismatch%20report`}
                  className="inline-block mt-3 text-sm text-forest underline underline-offset-4"
                >
                  ✉ Reply by email
                </a>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
